
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Chrome } from 'lucide-react';

interface FormData {
  email: string;
  confirmEmail: string;
  password: string;
}

interface SignupFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSubmit, loading, error }) => {
  const [googleLoading, setGoogleLoading] = useState(false);
  const form = useForm<FormData>();

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`
        }
      });
      
      if (error) {
        toast.error('Google signup failed: ' + error.message);
      }
    } catch (err) {
      toast.error('Failed to connect with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Google Sign Up Button */}
      {true && (
        <>
          <Button 
            type="button"
            variant="outline"
            className="w-full h-12 text-base font-medium border-2 hover:border-blue-300 transition-colors"
            onClick={handleGoogleSignup}
            disabled={googleLoading || loading}
          >
            <Chrome className="w-5 h-5 mr-3 text-red-500" />
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or</span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={form.handleSubmit((data) => {
        if (data.email !== data.confirmEmail) {
          toast.error('Email addresses do not match');
          return;
        }
        onSubmit(data);
      })} className="space-y-5">
        {/* Email Input - Mobile Optimized */}
        <div className="space-y-2">
          <label htmlFor="signup-email" className="block text-sm font-medium">
            Email Address
          </label>
          <Input
            id="signup-email"
            type="email"
            placeholder="your@email.com"
            autoComplete="email"
            className="h-12 text-base"
            {...form.register('email', { required: true })}
          />
        </div>

        {/* Confirm Email Input - Mobile Optimized */}
        <div className="space-y-2">
          <label htmlFor="signup-confirm-email" className="block text-sm font-medium">
            Confirm Email Address
          </label>
          <Input
            id="signup-confirm-email"
            type="email"
            placeholder="your@email.com"
            autoComplete="email"
            className="h-12 text-base"
            {...form.register('confirmEmail', { 
              required: true,
              validate: (value) => value === form.watch('email') || 'Email addresses must match'
            })}
          />
        </div>

        {/* Password Input - Mobile Optimized */}
        <div className="space-y-2">
          <label htmlFor="signup-password" className="block text-sm font-medium">
            Password
          </label>
          <Input
            id="signup-password"
            type="password"
            placeholder="At least 6 characters"
            autoComplete="new-password"
            className="h-12 text-base"
            {...form.register('password', { required: true, minLength: 6 })}
          />
        </div>

        {/* Create Account Button - Mobile Optimized */}
        <Button 
          type="submit" 
          className="w-full h-12 text-base font-medium bg-blue-600 hover:bg-blue-700 text-white mt-6" 
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>

        {/* Error Display */}
        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700 text-center">{error}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default SignupForm;
